import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApproval } from '../context/ApprovalContext';
import { ArrowLeft, CheckCircle, XCircle, Paperclip } from 'lucide-react';

const FIELD_LABELS = {
  // 공통
  docTitle:'제목', title:'제목', content:'내용', reason:'사유', remark:'비고',
  // 기안자 정보
  dept:'부서', grade:'직급', pmName:'PM 성함', preMeeting:'사전 면담',
  // 날짜
  substitute:'대체자',
  startDate:'시작일', endDate:'종료일', joinDate:'입사일', resignDate:'퇴사일',
  eventDate:'경조사 발생일', receiveDate:'수령 희망일',
  contractStart:'계약 시작일', contractEnd:'계약 종료일',
  tripStart:'출장 시작일', tripEnd:'출장 종료일',
  // 경조금
  eventType:'신청 사유', relation:'신청인과의 관계', targetName:'관계인 성명',
  // 명함
  nameEn:'영문명', receiveMethod:'수령 방법', receiveAddress:'수령 주소',
  // 물품
  projectName:'프로젝트명', receiveAddress:'수령지 주소', receiverInfo:'수령인/연락처',
  // 예산
  amount:'예산 신청금액', cardNo:'법인카드 번호', preApproval:'본부장 사전 승인',
  budgetBasis:'금액 산출 근거', totalBudget:'총 출장 예산',
  destination:'출장 지역', totalBudget:'총 출장 예산',
  // 계약
  contractName:'계약명', client:'계약처명', solution:'솔루션',
  contractAmount:'총 계약금액', purchaseAmount:'총 매입금액',
  totalMM:'총 투입공수(M/M)', totalMD:'총 투입공수(M/D)',
  discount:'할인율', cpi:'적용 CPI', clientContact:'고객사 담당자',
  // 제증명
  certEmploy:'재직증명서', certCareer:'경력증명서',
  certWithhold:'원천징수영수증', certWithholdYear:'귀속년도',
  certEtc:'기타 서류', certEtcName:'필요서류명',
  purpose:'용도', submitTo:'제출처',
  // 휴직
  leaveType:'휴직 종류',
  // 정보보안서약
  clientName:'고객사명',
  // 인사명령
  personnelRows:'인사명령 대상자',
  // 지출결의서
  expenseRows:'지출 내역', totalAmount:'총 지출 합계',
  // 출장
  travelerRows:'출장자 정보',
  // 물품신청
  items:'신청 물품',
};

// 특수 필드 렌더링
function renderFieldValue(key, value) {
  if (value === null || value === undefined || value === '') return '-';

  // 체크박스(boolean)
  if (typeof value === 'boolean') return value ? '✅ 동의' : '미동의';

  // 배열/테이블 타입
  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    const first = value[0];
    if (typeof first !== 'object') return value.join(', ');

    // 인사명령 테이블
    if (key === 'personnelRows') {
      const rows = value.filter(r => r.name);
      if (rows.length === 0) return '-';
      return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 6 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['소속','대상자','직급','투입일','종료일','담당업무','투입형태','비고'].map(h => (
                <th key={h} style={{ padding: '4px 8px', border: '1px solid #e5e7eb', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {[r.org, r.name, r.grade, r.startDate, r.endDate, r.task, r.type, r.remark].map((v, j) => (
                  <td key={j} style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>{v || '-'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    // 지출결의서 테이블
    if (key === 'expenseRows') {
      const rows = value.filter(r => r.date || r.amount);
      if (rows.length === 0) return '-';
      return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 6 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['지출일자','지출내역','거래처명','사용자','금액'].map(h => (
                <th key={h} style={{ padding: '4px 8px', border: '1px solid #e5e7eb', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {[r.date, r.category, r.vendor, r.user, r.amount ? r.amount + '원' : '-'].map((v, j) => (
                  <td key={j} style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>{v || '-'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    // 출장자 테이블
    if (key === 'travelerRows') {
      const rows = value.filter(r => r.name);
      if (rows.length === 0) return '-';
      return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 6 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['성명','직급','출장시작','출장종료','출장예산'].map(h => (
                <th key={h} style={{ padding: '4px 8px', border: '1px solid #e5e7eb', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {[r.name, r.grade, r.startDate, r.endDate, r.budget ? r.budget + '원' : '-'].map((v, j) => (
                  <td key={j} style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>{v || '-'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    // 물품 테이블
    if (key === 'items') {
      const rows = value.filter(r => r.productName || r.category !== '--');
      if (rows.length === 0) return '-';
      return (
        <div style={{ marginTop: 6 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: 8, marginBottom: 6, fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>품목{i+1}: {r.category}</span> | {r.productName} | 수량: {r.quantity} | 단가: {r.unitPrice} | 총액: {r.total}
            </div>
          ))}
        </div>
      );
    }

    return value.length + '건';
  }

  // 객체
  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
}

export default function ApprovalDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { approvals, fetchApprovals, updateApproval, APPROVAL_TYPES } = useApproval();
  const navigate = useNavigate();
  const [comment, setComment] = useState('');
  const [acting, setActing] = useState(false);
  const [leaveData, setLeaveData] = useState(null);

  useEffect(() => {
    if (approvals.length === 0) fetchApprovals();
  }, [fetchApprovals, approvals.length]);

  const approval = approvals.find(a => String(a.id) === String(id));

  // 휴가 결재인 경우 leaves API에서 데이터 가져오기
  useEffect(() => {
    if (approval?.type === '휴가신청') {
      fetch(`/api/leaves?userId=${approval.author.id}`)
        .then(r => r.json())
        .then(data => {
          const leave = data.find(l => l.id === approval.leaveId || l.title === approval.title);
          if (leave) setLeaveData(leave);
        }).catch(() => {});
    }
  }, [approval]);

  if (!approval) return (
    <div className="page container">
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <p style={{ fontSize: 16, color: '#9ca3af' }}>결재 데이터를 불러오는 중...</p>
        <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={() => navigate('/list')}>목록으로 돌아가기</button>
      </div>
    </div>
  );

  const TYPE_LABEL_MAP = {
    'vacation': '휴가신청', 'leave': '휴가신청',
    'condolence': '경조금지급신청서', 'official_doc': '공문기안서',
    'work_doc': '업무기안서', 'resignation': '사직서',
    'supplies': '물품신청서', 'namecard': '명함신청서',
    'expenditure': '지출결의서', 'business_trip': '출장예산신청서',
    'budget_req': '예산신청서', 'personnel': '인사명령신청서',
    'certificate': '제증명신청서', 'leave_req': '휴직신청서',
    'security_pledge': '정보보안서약서',
    'project_contract': '프로젝트계약품의서', 'project_purchase': '프로젝트매입계약요청서',
    'maintenance_contract': '유지보수계약품의서', 'maintenance_purchase': '유지보수매입계약요청서',
  };
  const typeInfo = APPROVAL_TYPES.find(t => t.id === approval.type);
  const typeLabel = typeInfo?.label || TYPE_LABEL_MAP[approval.type] || approval.type;
  const statusLabel = { pending: '대기중', approved: '승인', rejected: '반려' };
  const statusColor = { pending: '#d97706', approved: '#059669', rejected: '#dc2626' };
  const statusBg = { pending: '#fef3c7', approved: '#d1fae5', rejected: '#fee2e2' };
  const currentApprover = approval.approvers[approval.currentStep];
  const canAct = approval.status === 'pending' && Number(currentApprover?.id) === Number(user?.id);

  const handleAction = async (action) => {
    setActing(true);
    await updateApproval(approval.id, user.id, action, comment);
    setActing(false);
    setComment('');
  };

  // 표시할 formData 필터링 (빈값, 테이블 이미 처리된 항목 제외)
  const formEntries = Object.entries(approval.formData || {}).filter(([k, v]) => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'boolean' && !v) return false;
    return true;
  });

  return (
    <div className="page container">
      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
          <ArrowLeft size={14} /> 뒤로
        </button>
        <div style={{ flex: 1 }}>
          <h1 className="page-title">{typeInfo?.icon} {approval.title}</h1>
          <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: statusBg[approval.status], color: statusColor[approval.status] }}>
            {statusLabel[approval.status]}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        <div>
          {/* 기본 정보 */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>결재 내용</h2>

            {/* 기안자 정보 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '8px 24px', padding: '12px 0', borderBottom: '1px solid #f3f4f6', marginBottom: 12 }}>
              {[
                ['기안자', `${approval.author.name} (${approval.author.dept} · ${approval.author.position})`],
                ['기안일', approval.createdAt?.split('T')[0]],
                ['문서 유형', typeLabel],
                ['문서번호', approval.id?.split('-')[0].toUpperCase()],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                  <span style={{ color: '#9ca3af', minWidth: 70, flexShrink: 0 }}>{label}</span>
                  <span style={{ color: '#374151' }}>{value}</span>
                </div>
              ))}
            </div>

            {/* 양식 데이터 */}
            {formEntries.length > 0 && (
              <table style={{ fontSize: 13, width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {formEntries.map(([k, v]) => (
                    <tr key={k} style={{ borderBottom: '1px solid #f9fafb' }}>
                      <td style={{ color: '#6b7280', padding: '8px 0', width: 130, verticalAlign: 'top', fontWeight: 500 }}>
                        {FIELD_LABELS[k] || k}
                      </td>
                      <td style={{ padding: '8px 0 8px 12px', wordBreak: 'break-word', color: '#374151' }}>
                        {renderFieldValue(k, v)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* 휴가 결재 특별 표시 */}
            {approval.type === '휴가신청' && approval.formData && (
              <div style={{ marginTop: 12, padding: '14px 16px', background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0369a1', marginBottom: 10 }}>휴가 상세 정보</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '6px 20px', fontSize: 13 }}>
                  <div><span style={{ color: '#6b7280' }}>휴가유형: </span><span style={{ fontWeight: 500 }}>{approval.formData.leaveType}</span></div>
                  <div><span style={{ color: '#6b7280' }}>신청휴가수: </span><span style={{ fontWeight: 600, color: '#4f46e5' }}>{approval.formData.totalDays}d ({approval.formData.totalDays * 8}h)</span></div>
                  <div><span style={{ color: '#6b7280' }}>기간: </span><span>{approval.formData.dateRange || '-'}</span></div>
                  {approval.formData.reason && <div><span style={{ color: '#6b7280' }}>사유: </span><span>{approval.formData.reason}</span></div>}
                </div>
                {approval.formData.dates?.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>신청일 목록</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {approval.formData.dates.map((d, i) => {
                        const dow = ['일','월','화','수','목','금','토'][new Date(d.date).getDay()];
                        return (
                          <span key={i} style={{ fontSize: 11, padding: '3px 8px', background: '#eff6ff', color: '#1e40af', borderRadius: 6 }}>
                            {d.date}({dow}) - {d.option}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 첨부파일 */}
            {approval.attachedFiles?.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>
                  <Paperclip size={13} style={{ marginRight: 4 }} />첨부파일 {approval.attachedFiles.length}개
                </div>
                {approval.attachedFiles.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f9fafb', fontSize: 13 }}>
                    <Paperclip size={12} color="#4f46e5" />
                    <span style={{ flex: 1, color: '#374151' }}>{f.name}</span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{f.size ? (f.size / 1024).toFixed(1) + ' KB' : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 결재 처리 */}
          {canAct && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>결재 처리</h2>
              <div className="form-group">
                <label className="form-label">의견 (선택)</label>
                <textarea className="form-textarea" placeholder="의견을 입력하세요..." value={comment}
                  onChange={e => setComment(e.target.value)} style={{ minHeight: 80 }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-success" style={{ flex: 1, justifyContent: 'center' }}
                  disabled={acting} onClick={() => handleAction('approved')}>
                  <CheckCircle size={16} /> {acting ? '처리 중...' : '승인'}
                </button>
                <button className="btn btn-danger" style={{ flex: 1, justifyContent: 'center' }}
                  disabled={acting} onClick={() => handleAction('rejected')}>
                  <XCircle size={16} /> {acting ? '처리 중...' : '반려'}
                </button>
              </div>
            </div>
          )}

          {/* 이미 처리된 결재 */}
          {!canAct && approval.status !== 'pending' && (
            <div className="card" style={{ marginBottom: 16, background: approval.status === 'approved' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${approval.status === 'approved' ? '#86efac' : '#fca5a5'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {approval.status === 'approved'
                  ? <CheckCircle size={20} color="#059669" />
                  : <XCircle size={20} color="#dc2626" />}
                <span style={{ fontSize: 15, fontWeight: 600, color: approval.status === 'approved' ? '#059669' : '#dc2626' }}>
                  {approval.status === 'approved' ? '최종 승인 완료' : '반려됨'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 결재선 */}
        <div className="card" style={{ height: 'fit-content' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>결재선</h2>

          {/* 기안자 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
            <div className="avatar" style={{ background: '#6b7280', flexShrink: 0 }}>{approval.author.name[0]}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{approval.author.name}</div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>{approval.author.position} · 기안</div>
            </div>
            <span style={{ fontSize: 11, color: '#059669', fontWeight: 500 }}>✓ 제출</span>
          </div>

          {/* 결재자들 */}
          {approval.approvers.map((ap, i) => {
            const isActive = i === approval.currentStep && approval.status === 'pending';
            const bgColor = ap.status === 'approved' ? '#10b981' : ap.status === 'rejected' ? '#ef4444' : isActive ? '#4f46e5' : '#d1d5db';
            const statusText = ap.status === 'approved' ? '승인' : ap.status === 'rejected' ? '반려' : isActive ? '처리중' : '대기';
            const statusTxtColor = ap.status === 'approved' ? '#059669' : ap.status === 'rejected' ? '#dc2626' : isActive ? '#4f46e5' : '#9ca3af';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 0',
                borderBottom: i < approval.approvers.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                <div className="avatar" style={{ flexShrink: 0, background: bgColor, color: '#fff' }}>
                  {ap.status === 'approved' ? '✓' : ap.status === 'rejected' ? '✗' : i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{ap.name}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{ap.dept} · {ap.position}</div>
                  {ap.comment && (
                    <div style={{ fontSize: 12, color: '#374151', marginTop: 4, padding: '6px 8px', background: '#f9fafb', borderRadius: 6 }}>
                      "{ap.comment}"
                    </div>
                  )}
                  {ap.date && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{ap.date?.split('T')[0]}</div>}
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: statusTxtColor }}>{statusText}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
